import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { getPropertiesList } from 'entities/Property/model/selectors/PropertySelectors';
import {
    getRouteForbidden,
    getRouteReportsSpendSummary,
    getRouteReportsSpendSummaryCategory,
    getRouteReportsSpendSummaryProduct,
    getRouteReportsSpendSummaryProperty,
} from 'shared/const/router';
import { formatReportPercent } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import {
    ReportDrillInMetrics,
    ReportDrillInSections,
} from 'shared/ui/ReportDrillInLayout';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { Page } from 'widgets/Page';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';
import {
    getSpendCategoryTitle,
    mergeSpendDrillInScope,
} from 'widgets/SpendSummaryReport/model/types/spendDrillIn';
import { SpendSummaryCategoriesSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryCategoriesSection';
import { SpendSummaryProductsSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryProductsSection';
import { SpendSummaryPropertiesSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryPropertiesSection';
import { SpendSummaryTotalSpendMetric } from 'widgets/SpendSummaryReport/ui/SpendSummaryTotalSpendMetric';

import { useReportsPageSpendSummarySupplier } from '../model/hooks/useReportsPageSpendSummarySupplier';

import type { SpendProductRowDto } from 'entities/ReportSpendSummary';
import type { ReportWidgetInteractionPayload } from 'widgets/ComposableReportWidget';
import type { SpendDrillInLocationState } from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

const ReportsPageSpendSummarySupplier = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const properties = useSelector(getPropertiesList) ?? [];
    const {
        isSpendSummaryVisible,
        supplierId,
        supplierName,
        loading,
        summary,
        byCategory,
        products,
        totalSpend,
        totalSpendPercent,
        orderCount,
        productsCount,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
        locationState,
    } = useReportsPageSpendSummarySupplier();

    const childScope = useMemo(
        () =>
            mergeSpendDrillInScope(locationState.scope, {
                supplierIds: supplierId ? [supplierId] : undefined,
            }),
        [locationState.scope, supplierId],
    );

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSpendSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleCategorySelect = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const categoryId = payload.item.meta?.categoryId;
            if (typeof categoryId !== 'string') {
                return;
            }

            const state: SpendDrillInLocationState = {
                categoryTitle: getSpendCategoryTitle(
                    payload.item.meta?.categoryTitle,
                    payload.item.label,
                ),
                contextTotalSpend: summary?.totalSpend,
                scope: childScope,
            };
            navigate(getRouteReportsSpendSummaryCategory(categoryId), {
                state,
            });
        },
        [childScope, navigate, summary?.totalSpend],
    );

    const handlePropertySelect = useCallback(
        ({
            propertyId,
            propertyName,
        }: {
            propertyId: string;
            propertyName?: string;
        }) => {
            const state: SpendDrillInLocationState = {
                propertyName,
                contextTotalSpend: summary?.totalSpend,
                scope: childScope,
            };
            navigate(getRouteReportsSpendSummaryProperty(propertyId), {
                state,
            });
        },
        [childScope, navigate, summary?.totalSpend],
    );

    const handleProductRowClick = useCallback(
        (sku: string, product: SpendProductRowDto) => {
            const state: SpendDrillInLocationState = {
                product,
                contextTotalSpend: summary?.totalSpend,
                scope: childScope,
            };
            navigate(getRouteReportsSpendSummaryProduct(sku), { state });
        },
        [childScope, navigate, summary?.totalSpend],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSpendSummaryVisible === undefined) {
        return null;
    }

    if (!isSpendSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!supplierId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSpendSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-spend-summary-supplier"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={supplierName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('spendSummary.pdfTitle')}
                    backButtonId="back-to-spend-summary-button"
                />

                <ReportDrillInMetrics>
                    <SpendSummaryTotalSpendMetric
                        summary={summary}
                        primaryAmount={totalSpend}
                        loading={loading}
                        showSavingsDetail={false}
                    />
                    <ReportMetric
                        title={t('spendSummary.percentOfTotalSpend')}
                        primary={formatReportPercent(totalSpendPercent)}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('Orders')}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                    <ReportMetric
                        title={t('Products')}
                        primary={productsCount.toLocaleString()}
                        loading={loading}
                        minHeight="100px"
                    />
                </ReportDrillInMetrics>

                <ReportDrillInSections>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: '360px' }}>
                            <SpendSummaryCategoriesSection
                                byCategory={byCategory}
                                loading={loading}
                                onCategorySelect={handleCategorySelect}
                                tableOnly
                                tableColumnIds={[
                                    'category',
                                    'spend',
                                    'percentSpend',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: '360px' }}>
                            <SpendSummaryPropertiesSection
                                byProperties={summary?.byProperties ?? []}
                                properties={properties}
                                loading={loading}
                                dateFrom={dateFrom}
                                dateTo={dateTo}
                                tableOnly
                                tableColumnIds={[
                                    'property',
                                    'spend',
                                    'spendPerUnit',
                                    'percentSpend',
                                ]}
                                onPropertySelect={handlePropertySelect}
                                showBottomBorder={false}
                            />
                        </Box>
                    </Box>
                    <SpendSummaryProductsSection
                        products={products}
                        totalSpend={summary?.totalSpend}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'properties',
                            'quantity',
                            'totalSpend',
                            'percentSpend',
                        ]}
                        showBottomBorder={false}
                    />
                    <SavingsSummaryProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </ReportDrillInSections>
            </Box>
        </Page>
    );
};

export default ReportsPageSpendSummarySupplier;
